/**
 * Billing API client
 *
 * Handles billing/subscription API calls. All requests go through the
 * centralized API SDK for consistent authentication, token refresh, and
 * 401 handling.
 *
 * Error Handling:
 * All methods throw ApiClientError on failure (mirrors domainsApi). Use
 * try/catch blocks or .catch() to handle errors. The ApiClientError provides:
 * - Structured error information (code, type, message)
 * - Validation violations for form errors
 * - Type-checking methods (isValidationError(), isNotFound(), etc.)
 *
 * Exception: `syncSiteSubscription` keeps the legacy `ApiResponse<T>`
 * envelope — see its JSDoc.
 */

import { api, isApiClientError } from './sdk';
import {
  PaymentMethod,
  Invoice,
  BillingPortalSession,
  ApiResponse,
} from '../types/billing';
import { buildListQuery, type Paginated, type PaginationParams } from './pagination';

// Re-export error handling utilities for consumers
export { ApiClientError, isApiClientError } from './sdk';

// ============================================
// ORG-LEVEL SUBSCRIPTIONS (the /billing page)
// ============================================

/**
 * One row in the org-level subscription list. Mirrors the backend
 * `GET /api/billing/subscriptions` payload.
 *
 * - `siteId` / `siteName` / `siteStatus` / `templateName` / `tier` are null for
 *   subscriptions whose `metadata.siteId` doesn't resolve to a local row
 *   (e.g. created manually in the Stripe dashboard).
 * - `monthlyPrice` is in **cents** as returned by Stripe's `unit_amount`.
 */
export interface SubscriptionRow {
  stripeSubscriptionId: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  currentPeriodEnd: string;
  monthlyPrice: number | null;
  siteId: string | null;
  siteName: string | null;
  siteStatus: string | null;
  templateName: string | null;
  tier: 'standard' | 'premium' | null;
}

/**
 * List all Stripe subscriptions for the org (paginated, newest first).
 * @throws {ApiClientError} On API failure
 */
export const listSubscriptions = (
  params: PaginationParams = {},
): Promise<Paginated<SubscriptionRow>> =>
  api.get<Paginated<SubscriptionRow>>(
    `/api/billing/subscriptions${buildListQuery(params)}`,
  );

// ============================================
// BILLING PORTAL
// ============================================

/**
 * Create a Stripe customer-portal session for the current org.
 * Used by the "Manage in Stripe" button on the /billing page.
 * @throws {ApiClientError} On API failure
 */
export const createBillingPortalSession = (): Promise<BillingPortalSession> =>
  api.post<BillingPortalSession>('/api/billing/portal/create');

// ============================================
// INVOICES (read-only; sourced from Stripe)
// ============================================

/**
 * Get invoices for the current org. Sourced from Stripe at request time.
 * @throws {ApiClientError} On API failure
 */
export const getInvoices = (limit: number = 10): Promise<Invoice[]> =>
  api.get<Invoice[]>(`/api/billing/invoices?limit=${limit}`);

// ============================================
// PAYMENT METHODS (read-only; sourced from Stripe)
// ============================================

/**
 * Get payment methods for the current org. Sourced from Stripe.
 * @throws {ApiClientError} On API failure
 */
export const getPaymentMethods = (): Promise<PaymentMethod[]> =>
  api.get<PaymentMethod[]>('/api/billing/payment-methods');

/**
 * Set default payment method.
 * @throws {ApiClientError} On API failure
 */
export const setDefaultPaymentMethod = (
  paymentMethodId: string,
): Promise<PaymentMethod> =>
  api.post<PaymentMethod>(
    `/api/billing/payment-methods/${paymentMethodId}/set-default`,
  );

// ============================================
// PER-SITE BILLING (publish / republish / upgrade / cancel / reactivate)
// ============================================

/**
 * Initiate publish for a draft site. The backend decides free vs paid:
 *  - free path: status flips to `live`, returns `{ status: 'live', checkoutUrl: null }`
 *  - paid path: returns `{ status: 'draft', checkoutUrl: <stripe-checkout-url> }`
 *    and the caller redirects.
 * @throws {ApiClientError} On API failure
 */
export const publishSite = (
  siteId: string,
): Promise<{ status: string; checkoutUrl: string | null }> =>
  api.post(`/api/sites/${siteId}/publish`);

/**
 * Republish a `suspended` or `offline` site. Same shape as `publishSite`.
 * @throws {ApiClientError} On API failure
 */
export const republishSite = (
  siteId: string,
): Promise<{ status: string; checkoutUrl: string | null }> =>
  api.post(`/api/sites/${siteId}/republish`);

/**
 * Re-dispatch the publish workflow for a site that has been stuck in
 * 'publishing' for >5min. Backend enforces the same staleness check.
 * @throws {ApiClientError} On API failure
 */
export const retryPublishSite = (siteId: string): Promise<{ status: string }> =>
  api.post(`/api/sites/${siteId}/retry-publish`);

/**
 * Upgrade a `live` free site (e.g. to add a custom domain). Always paid path.
 * @throws {ApiClientError} On API failure
 */
export const upgradeSite = (siteId: string): Promise<{ checkoutUrl: string }> =>
  api.post(`/api/sites/${siteId}/upgrade`);

/**
 * Reconcile site subscription state directly from Stripe (independent of the
 * webhook). Used right after a Stripe Checkout redirect so the UI doesn't have
 * to race the `checkout.session.completed` webhook.
 *
 * NOTE: unlike the rest of this module, this returns the legacy
 * `ApiResponse<T>` envelope instead of throwing — its caller (SiteRoute's
 * Stripe-return effect) depends on that contract.
 */
export async function syncSiteSubscription(
  siteId: string
): Promise<ApiResponse<{
  synced: boolean;
  reason?: 'no-customer' | 'no-subscription';
  subscriptionId?: string;
  status?: string;
  /** True when this sync was the writer that flipped the row to `live`
   *  and dispatched the deploy workflow. Lets the caller skip further
   *  retries — there's nothing more to reconcile. */
  deployTriggered?: boolean;
}>> {
  try {
    const data = await api.post<{
      synced: boolean;
      reason?: 'no-customer' | 'no-subscription';
      subscriptionId?: string;
      status?: string;
      deployTriggered?: boolean;
    }>(`/api/sites/${siteId}/sync-subscription`);
    return { success: true, data };
  } catch (error) {
    if (isApiClientError(error)) {
      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Per-site subscription state. `paid` is true iff there's an active sub.
 * @throws {ApiClientError} On API failure
 */
export const getSiteSubscription = (
  siteId: string,
): Promise<{
  tier: 'standard' | 'premium';
  paid: boolean;
  status: string;
  amount: number;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  plan: {
    id: string;
    name: string;
    monthlyPrice: number;
  } | null;
}> => api.get(`/api/sites/${siteId}/subscription`);

/**
 * Cancel a site's subscription at period end.
 * @throws {ApiClientError} On API failure
 */
export const cancelSiteSubscription = (
  siteId: string,
): Promise<{
  cancelAt: string;
  message: string;
}> => api.post(`/api/sites/${siteId}/cancel-subscription`);

/**
 * Clear the cancel-at-period-end flag on a site's subscription.
 * @throws {ApiClientError} On API failure
 */
export const reactivateSiteSubscription = (
  siteId: string,
): Promise<{
  status: string;
  message: string;
}> => api.post(`/api/sites/${siteId}/reactivate-subscription`);

/**
 * Open the Stripe customer portal deep-linked to a site's subscription.
 * @throws {ApiClientError} On API failure
 */
export const createSiteBillingPortalSession = (
  siteId: string,
): Promise<BillingPortalSession> =>
  api.post<BillingPortalSession>(`/api/sites/${siteId}/billing-portal`);
